'use client';
import { FC, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { twJoin, twMerge } from 'tailwind-merge';
import { useRouter } from 'next/navigation';

import { Heading, Button, Text, Input, Svg } from '@/components';
import Modal from '@/layouts/Modal';
import { OptionTypes } from '@/features/ProductDetails/components/SelectOption';
import RemoveItem from '@/features/Cart/components/RemoveItem';
import { getProductOptions } from '@/features/ProductDetails/services/getProduct';
import { PRODUCT_TYPE_ENUMS } from '@/features/ProductDetails/constants/ProductType';
import ShelfItem from '@/features/Cart/components/ShelfItem';
import GiftBoxProductsModal from './GiftBoxProductsModal';
import BentoBoxProductsModal from './BentoBoxProductsModal';
import { useCart } from '@/context/cartContext';
import { useToggle } from '@/hooks';
import { getOptionByValue, getWeightWithUnit } from '@/utils/helper';
import { OUT_OF_STOCK_CODE, PRODUCT_LAYOUT_ENUMS } from '@/constants';
import { IMAGE_URI } from '@/config/endpoints';
import { CalendarIcon, ClockIcon, LocationPinIcon } from '@/icons';
import dayjs from 'dayjs';
import { useCleverTap } from '@/context/clevertapContext';
import { useMakeYourGiftStore } from '@/store/make-your-gift-store';

type ProductTypes = 'simple' | 'configurable' | 'bundle';

export type LayoutTypes =
  | 'cart'
  | 'summary'
  | 'studio-summary'
  | 'events-summary'
  | 'studio-order-status';

export interface ProductItemPropTypes {
  name: string;
  sku: string;
  id?: number;
  item_id?: number;
  quote_id?: string;
  price?: number;
  product_type?: ProductTypes;
  parent_item_id: string | null;
  qty: number;
  image?: string;
  quantity?: string;
  product_option?: any;
  custom_attributes?: any;
  extension_attributes?: {
    available_qty?: number;
    product_image: string;
    attribute_options: any;
    product_url: string;
    in_stock: number;
    is_gift_hamper: string;
    is_gift_box?: '1' | '0';
    is_greeting_card: number;
    parent_sku?: string;
    parent_url_key?: string;
    product_remark?: string | 'freebie';
    weight?: string;
    weight_unit?: string;
    product_links: any[];
    recommended_categories: any;
  };
  event_start_date?: string;
  event_end_date?: string;
  location?: string;
  event_location?: string;
  date?: string;
  time?: string;
  in_store?: boolean;
  order_items?: any;
  delivery_date?: string;
  street?: string;
  giftMessages?: any;
  giftHamper?: any;
  postcode?: string;
  city?: string;
}

type ClassesTypes = {
  image?: string;
  title?: string;
  root: string;
};

interface PropTypes {
  item: ProductItemPropTypes;
  disabled?: boolean;
  layout?: LayoutTypes;
  classes?: ClassesTypes;
  label?: string;
}

const DISABLED_CLASSNAMES = 'cursor-not-allowed opacity-60';

const ProductPrice = ({
  price,
  isGiftBox,
  getGiftBoxTotalPrice,
}: ProductItemPropTypes & {
  isGiftBox?: boolean;
  getGiftBoxTotalPrice?: () => number;
}) => (
  <Text className='text-sm'>
    ₹{isGiftBox && getGiftBoxTotalPrice ? getGiftBoxTotalPrice() : price}
  </Text>
);

const ProductQuantity = ({ qty }: ProductItemPropTypes) => (
  <Text className='text-[10px] px-0'>Quantity : {qty}</Text>
);

const CheckoutProductCard: FC<PropTypes> = ({
  layout = PRODUCT_LAYOUT_ENUMS.summary,
  classes,
  disabled = false,
  item,
  label,
}) => {
  const router = useRouter();
  const { pushEvent } = useCleverTap();
  const {
    addToCartProduct,
    toggleCartSidebar,
    removeFromCart,
    cartValue,
    cartItems,
  } = useCart();
  const { syncFromCartItem } = useMakeYourGiftStore();
  const { value: showRemoveItemModal, toggleValue: toggleRemoveItemModal } =
    useToggle();
  const { value: showShelfItemModal, toggleValue: toggleShelfItemModal } =
    useToggle();
  const {
    value: showGiftBoxProductsModal,
    toggleValue: toggleGiftBoxProductsModal,
  } = useToggle();
  const {
    value: showBentoBoxProductsModal,
    toggleValue: toggleBentoBoxProductsModal,
  } = useToggle();
  const [packageOptions, setPackageOptions] = useState<OptionTypes[] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isGreetingCard =
    item?.extension_attributes?.is_greeting_card === 1 ? true : false;
  const isGiftBox = item?.extension_attributes?.is_gift_box === '1';
  const isBentoBox = item?.product_type === 'bundle';

  const onRemoveItemModalClose = () => toggleRemoveItemModal(false);
  const onShelfItemModalClose = () => toggleShelfItemModal(false);
  const onGiftBoxProductsModalClose = () => toggleGiftBoxProductsModal(false);
  const onBentoBoxProductsModalClose = () => toggleBentoBoxProductsModal(false);

  const getGiftBoxProducts = () => {
    if (!cartItems || cartItems?.length === 0) return [];
    return cartItems.filter(cartItem => {
      const isGiftBoxItem =
        String(cartItem?.extension_attributes?.gift_box_item_id) ===
        String(item?.item_id);
      const isNotGiftBox = cartItem?.extension_attributes?.is_gift_box !== '1';
      const isNotGreetingCard =
        cartItem?.extension_attributes?.is_greeting_card !== 1;
      return isGiftBoxItem && isNotGiftBox && isNotGreetingCard;
    });
  };

  const getGiftBoxTotalPrice = () => {
    const giftBoxProducts = getGiftBoxProducts();
    const productsTotal = giftBoxProducts.reduce((sum, product) => {
      return sum + Number(product.price) * Number(product.qty);
    }, 0);
    // Add the gift box price itself
    return productsTotal + Number(item?.price || 0);
  };

  const sizeAttributes = useMemo(() => {
    if (item?.product_type === PRODUCT_TYPE_ENUMS.configurable) {
      return item?.product_option?.extension_attributes
        .configurable_item_options[0];
    }
  }, [item]);

  const getPackageSize = async () => {
    const data: any = await getProductOptions(sizeAttributes?.option_id);
    return data;
  };

  const selectedSize: any = useMemo(() => {
    if (layout === PRODUCT_LAYOUT_ENUMS.summary && packageOptions?.length) {
      const selected = getOptionByValue(
        packageOptions,
        sizeAttributes?.option_value
      );
      return selected;
    }
  }, [packageOptions]);

  useEffect(() => {
    if (item?.product_type === PRODUCT_TYPE_ENUMS.configurable) {
      (async () => {
        const data: any = await getPackageSize();
        setPackageOptions(data?.options);
      })();
    }
  }, []);

  const request: any = {
    ...item,
    type_id: item.product_type,
    gift: {},
    ...(item.product_type === PRODUCT_TYPE_ENUMS.configurable && {
      size: {
        option_id: sizeAttributes?.option_id,
        option_value: sizeAttributes?.option_value,
      },
    }),
  };

  const onRoute = () => {
    if (
      layout !== PRODUCT_LAYOUT_ENUMS.studioOrderStatus &&
      layout !== PRODUCT_LAYOUT_ENUMS.studioSummary &&
      !isGreetingCard
    ) {
      const giftAttributes =
        item?.product_option?.extension_attributes?.custom_options?.[0];
      toggleCartSidebar(false);
      if (item?.product_type === PRODUCT_TYPE_ENUMS.configurable) {
        if (!giftAttributes || giftAttributes.option_value === '0') {
          router.push(
            `/shop/product/${item?.extension_attributes?.parent_url_key}?selected=${item?.sku}`
          );
        } else {
          router.push(
            `/gifting/product/${item?.extension_attributes?.parent_url_key}?selected=${item?.sku}`
          );
        }
      } else {
        if (isBentoBox) {
          router.push(
            `/gifting/quartet-box/${item?.extension_attributes?.product_url}`
          );
        } else if (!giftAttributes || giftAttributes.option_value === '0') {
          router.push(
            `/shop/product/${item?.extension_attributes?.product_url}`
          );
        } else {
          router.push(
            `/gifting/product/${item?.extension_attributes?.product_url}`
          );
        }
      }
    }
  };

  const ProductTitle = ({ className }: any) => {
    return (
      <div onClick={isGiftBox ? () => {} : onRoute}>
        <Heading
          as='h4'
          className={twMerge(
            className,
            'line-clamp-2 cursor-pointer text-sm font-normal hover:underline text-left',
            (layout === PRODUCT_LAYOUT_ENUMS.studioOrderStatus ||
              layout === PRODUCT_LAYOUT_ENUMS.studioSummary ||
              isGreetingCard) &&
              'cursor-default hover:no-underline',
            isGiftBox && 'cursor-default hover:no-underline'
          )}>
          {layout === PRODUCT_LAYOUT_ENUMS.eventsSummary
            ? item?.order_items[0]?.name
            : item?.name}{' '}
          {/* <span className='opacity-50'>
            {item?.extension_attributes?.weight &&
              getWeightWithUnit(
                item?.extension_attributes?.weight,
                item?.extension_attributes?.weight_unit
              )}
          </span> */}
        </Heading>
      </div>
    );
  };

  const handleEditGiftBox = () => {
    // Trigger event-based sync before navigating
    if (isGiftBox && cartItems) {
      syncFromCartItem(item, cartItems);
    }
    router.push(`/gifting/build-your-own-box/select-box?isEdit=true`);
  };

  return (
    <>
      {layout === PRODUCT_LAYOUT_ENUMS.studioOrderStatus && (
        <Heading as='h4' className='mb-2 text-lg font-semibold'>
          {label}
        </Heading>
      )}
      <div
        aria-label='product-card'
        className={twMerge(classes?.root, 'flex items-start')}>
        <div
          className={twMerge(
            classes?.image,
            'relative h-10 w-10 cursor-pointer overflow-hidden bg-pale-rose',
            item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE &&
              'opacity-60',
            item?.extension_attributes?.available_qty &&
              item?.extension_attributes?.available_qty < item?.qty &&
              'opacity-60'
          )}
          aria-label='product-image'
          onClick={isGiftBox ? () => {} : onRoute}>
          <Image
            alt={item?.name}
            title={item?.name}
            fill
            src={`${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${
              layout === PRODUCT_LAYOUT_ENUMS.studioSummary ||
              layout === PRODUCT_LAYOUT_ENUMS.studioOrderStatus
                ? item?.image
                : layout === PRODUCT_LAYOUT_ENUMS.eventsSummary
                  ? item?.order_items[0]?.image
                  : item?.extension_attributes?.product_image
            }`}
            style={{ objectFit: 'cover' }}
            sizes='25vw'
            className={twJoin(disabled && DISABLED_CLASSNAMES)}
          />
        </div>
        {layout === PRODUCT_LAYOUT_ENUMS.studioOrderStatus && (
          <ProductTitle className='text-base md:hidden' />
        )}

        {layout === PRODUCT_LAYOUT_ENUMS.summary && (
          <div
            aria-label='product-details'
            className={twMerge(
              'flex flex-1 flex-col',
              item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE &&
                'opacity-60'
            )}>
            <div className='w-full'>
              <div className='flex items-center justify-between w-full'>
                <ProductTitle className='text-sm' />
                <ProductPrice
                  {...item}
                  isGiftBox={isGiftBox}
                  getGiftBoxTotalPrice={getGiftBoxTotalPrice}
                />
              </div>
              <div className='mb-2 flex items-center'>
                <div className='flex items-center pl-3 gap'>
                  {item?.extension_attributes?.weight && (
                    <Text className='text-[10px] px-0'>
                      {getWeightWithUnit(
                        item?.extension_attributes?.weight,
                        item?.extension_attributes?.weight_unit
                      )}
                    </Text>
                  )}
                  {item?.extension_attributes?.is_gift_hamper !== '1' && (
                    <div className='opacity-10 text-xs font-semibold'>|</div>
                  )}
                  <ProductQuantity {...item} />
                  {isGiftBox && (
                    <Button
                      label='View Products'
                      variant='link'
                      onClick={() => toggleGiftBoxProductsModal()}
                      className='p-0 text-xs ml-2'
                    />
                  )}
                  {isGiftBox && (
                    <Button
                      label='Edit'
                      variant='link'
                      onClick={handleEditGiftBox}
                      className='p-0 text-xs ml-2'
                    />
                  )}
                  {isBentoBox && (
                    <Button
                      label='View Products'
                      variant='link'
                      onClick={() => toggleBentoBoxProductsModal()}
                      className='p-0 text-xs ml-2'
                    />
                  )}
                </div>
                {selectedSize && (
                  <Text className='text-xs'>{selectedSize?.label}</Text>
                )}
              </div>
            </div>
            {Boolean(
              item?.extension_attributes?.available_qty &&
              item?.extension_attributes?.available_qty < item?.qty
            ) && (
              <div
                className={twMerge(
                  'mr-4 rounded-sm text-black text-xs md:text-sm',
                  item?.extension_attributes?.available_qty &&
                    item?.extension_attributes?.available_qty < item?.qty &&
                    'opacity-80'
                )}>
                Oops! Only {item?.extension_attributes?.available_qty} left in
                stock. Please update the quantity to proceed.
              </div>
            )}
          </div>
        )}

        {layout === PRODUCT_LAYOUT_ENUMS.cart && (
          <>
            <div aria-label='product-details' className={twMerge('flex-1')}>
              <div className='space-y-4'>
                <div
                  className={twJoin(
                    `space-y-1`,
                    disabled && DISABLED_CLASSNAMES,
                    item?.extension_attributes?.in_stock ===
                      OUT_OF_STOCK_CODE && 'opacity-60'
                  )}>
                  <ProductTitle className={classes?.title} name={item?.name} />
                  {!isGreetingCard ? (
                    <ProductPrice {...item} />
                  ) : (
                    <>
                      <Text className='text-sm font-medium'>
                        Qty: {item?.qty}
                      </Text>
                      <ProductPrice {...item} />
                    </>
                  )}
                </div>
                <div
                  className={twMerge(
                    'flex items-center gap-x-6 px-3',
                    item?.extension_attributes?.in_stock ===
                      OUT_OF_STOCK_CODE && 'opacity-60'
                  )}>
                  {!isGreetingCard && (
                    <Input.ToggleCountField
                      defaultValue={item?.qty}
                      disabled={isLoading}
                      getCount={async (newValue: number) => {
                        setIsLoading(true);
                        // Checks if the new qty of the hamper is less than what we have in cart
                        request.qty = newValue;
                        if (newValue === 0) {
                          await removeFromCart(request);
                          setIsLoading(false);
                          return;
                        }
                        if (item.qty && item.qty < newValue) {
                          const body: any = { ...item, qty: newValue };
                          if (
                            item?.product_type ===
                            PRODUCT_TYPE_ENUMS.configurable
                          ) {
                            body.size = {
                              option_id: sizeAttributes.option_id,
                              option_value: sizeAttributes.option_value,
                            };
                          }
                          const req = await addToCartProduct(
                            body,
                            'put',
                            false,
                            {
                              showToast: false,
                            }
                          );
                          if (req.success) {
                            pushEvent({
                              event: 'Updated Product In Cart',
                              data: {
                                ['Timestamp']: new Date(),
                                ['Product Name']: item?.name,
                                ['Product ID']: item?.sku,
                                ['Product Price']: item.price,
                                ['Updated Quantity']: newValue,
                                ['Cart Amount']: cartValue + Number(item.price),
                              },
                            });
                          }
                        }
                        setIsLoading(false);
                      }}
                    />
                  )}
                </div>
                {item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE && (
                  <div
                    className={twMerge(
                      'mr-4 rounded-sm bg-[#EDEAE980] p-4',
                      item?.extension_attributes?.in_stock ===
                        OUT_OF_STOCK_CODE && 'opacity-60'
                    )}>
                    Unfortunately this product is sold out.
                  </div>
                )}
                <div className='flex items-center gap-3 px-3'>
                  <Button
                    label='Remove'
                    variant='link'
                    onClick={() => toggleRemoveItemModal()}
                    className='p-0 text-xs'
                  />
                  {!isGreetingCard && (
                    <Button
                      onClick={() => toggleShelfItemModal()}
                      label='Move To Shelf'
                      variant='link'
                      className='p-0 text-xs'
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {layout === PRODUCT_LAYOUT_ENUMS.studioSummary && (
          <div
            aria-label='product-details'
            className={twMerge(
              'flex flex-1',
              item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE &&
                'opacity-60'
            )}>
            <div>
              <ProductTitle className='text-base' />
              <ProductPrice {...item} />
              {label === 'Events' && (
                <div className='px-2'>
                  {item?.qty && Number(item?.qty)} Person
                </div>
              )}
              <div className='flex flex-col gap-y-2 px-2 pt-2'>
                <div className='flex w-fit items-start justify-start gap-1 rounded-2xl bg-[#EDEAE980] px-4 py-1 md:items-center'>
                  <div className='h-4 w-4 max-md:pt-1'>
                    <Svg className='text-[#262626]' viewBox='0 0 28 28'>
                      <CalendarIcon color='#262626' opacity='1' />
                    </Svg>
                  </div>
                  <Text className='px-0 text-sm text-[#262626]'>
                    {label === 'Kitchen Studio'
                      ? dayjs(item?.date).format('DD MMMM YYYY')
                      : dayjs(item?.event_start_date).format('DD MMMM YYYY')}
                  </Text>
                </div>
                <div className='flex w-fit items-start justify-start gap-1 rounded-2xl bg-[#EDEAE980] px-4 py-1 md:items-center'>
                  <div className='h-4 w-4 max-md:pt-1'>
                    <Svg className='stroke-[#262626]' viewBox='0 0 24 24'>
                      <ClockIcon />
                    </Svg>
                  </div>
                  <Text className='px-0 text-sm text-[#262626]'>
                    {label === 'Kitchen Studio'
                      ? item?.time
                      : `${dayjs(item?.event_start_date)
                          .add(5, 'hours')
                          .add(30, 'minutes')
                          .format('hh:mm A')} -
                        ${dayjs(item?.event_end_date)
                          .add(5, 'hours')
                          .add(30, 'minutes')
                          .format('hh:mm A')}`}
                  </Text>
                </div>
                <div className='flex w-fit items-start justify-start gap-1 rounded-2xl bg-[#EDEAE980] px-4 py-1 md:items-center'>
                  <div className='h-4 w-4 max-md:pt-1'>
                    <Svg className='stroke-[#262626]' viewBox='0 0 24 24'>
                      <LocationPinIcon />
                    </Svg>
                  </div>
                  <Text className='px-0 text-sm text-[#262626]'>
                    {item?.location}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        )}

        {layout === PRODUCT_LAYOUT_ENUMS.eventsSummary && (
          <div
            aria-label='product-details'
            className={twMerge(
              'flex flex-1',
              item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE &&
                'opacity-60'
            )}>
            <div>
              <ProductTitle className='text-base' />
              <ProductPrice {...item?.order_items[0]} />
              {label === 'Events' && (
                <div className='px-2'>
                  {item?.order_items[0]?.quantity} Person
                </div>
              )}
              <div className='flex flex-col gap-y-2 px-2 pt-2'>
                <div className='flex w-fit items-start justify-start gap-1 rounded-2xl bg-[#EDEAE980] px-4 py-1 md:items-center'>
                  <Svg className='h-4 w-4 text-[#262626]'>
                    <CalendarIcon color='#262626' opacity='1' />
                  </Svg>
                  <Text className='px-0 text-sm text-[#262626]'>
                    {label === 'Kitchen Studio'
                      ? dayjs(item?.date).format('DD MMMM YYYY')
                      : dayjs(item?.delivery_date).format('DD MMMM YYYY')}
                  </Text>
                </div>
                <div className='flex w-fit items-start justify-start gap-1 rounded-2xl bg-[#EDEAE980] px-4 py-1 md:items-center'>
                  <div className='h-4 w-4'>
                    <Svg className='stroke-[#262626]'>
                      <ClockIcon />
                    </Svg>
                  </div>
                  <Text className='px-0 text-sm text-[#262626]'>
                    {label === 'Kitchen Studio'
                      ? item?.time
                      : dayjs(item?.delivery_date).format('h A')}
                  </Text>
                </div>
                <div className='flex w-fit items-start justify-start gap-1 rounded-2xl bg-[#EDEAE980] px-4 py-1 md:items-center'>
                  <div className='h-4 w-4'>
                    <Svg>
                      <LocationPinIcon />
                    </Svg>
                  </div>
                  <Text className='px-0 text-sm text-[#262626]'>
                    {item?.street}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        )}

        {layout === PRODUCT_LAYOUT_ENUMS.studioOrderStatus && (
          <div
            aria-label='product-details'
            className={twMerge(
              'hidden md:flex md:flex-1',
              item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE &&
                'opacity-60'
            )}>
            <div className='px-2'>
              <ProductTitle className='text-base' />
              <div className='flex flex-col gap-y-4 py-2'>
                <div className='flex items-start justify-start'>
                  <div className='flex flex-col'>
                    <Text className='text-xs text-[#38383A99]'>Date</Text>
                    <Text className='font-semibold'>
                      {label === 'Kitchen Studio'
                        ? dayjs(item?.date)
                            .add(5, 'hours')
                            .add(30, 'minutes')
                            .format('MMM D, YYYY')
                        : dayjs(item?.event_start_date)
                            .add(5, 'hours')
                            .add(30, 'minutes')
                            .format('MMM D, YYYY')}
                    </Text>
                  </div>
                  <div className='flex flex-col'>
                    <Text className='text-xs text-[#38383A99]'>Time</Text>
                    <Text className='font-semibold'>
                      {label === 'Kitchen Studio'
                        ? item?.time
                        : `${dayjs(item?.event_start_date)
                            .add(5, 'hours')
                            .add(30, 'minutes')
                            .format('hh:mm A')} -
                        ${dayjs(item?.event_end_date)
                          .add(5, 'hours')
                          .add(30, 'minutes')
                          .format('hh:mm A')}`}
                    </Text>
                  </div>
                  {label === 'Event Details' && (
                    <div className='flex flex-col'>
                      <Text className='text-xs text-[#38383A99]'>
                        No. of Seat(s)
                      </Text>
                      <Text className='font-semibold'>
                        {`${Number(item?.quantity)} ${
                          Number(item?.quantity) === 1 ? 'Seat' : 'Seats'
                        } Booked`}
                      </Text>
                    </div>
                  )}
                </div>
                {(item?.location || item?.event_location) && (
                  <div className='flex flex-col'>
                    <Text className='text-xs text-[#38383A99]'>Venue</Text>
                    <Text className='font-semibold'>
                      {item?.location || item?.event_location}
                    </Text>
                  </div>
                )}
                <Text className='text-xs text-[#38383A99]'>
                  You will receive a confirmation via SMS, Whatsapp or Email
                  depending on your communication preference.
                </Text>
              </div>
            </div>
          </div>
        )}
      </div>

      {layout === PRODUCT_LAYOUT_ENUMS.studioOrderStatus && (
        <div
          aria-label='product-details'
          className={twMerge(
            'flex flex-1 md:hidden',
            item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE &&
              'opacity-60'
          )}>
          <div className='px-2'>
            <div className='flex flex-col gap-y-4 py-2'>
              <div className='flex items-start justify-start'>
                <div className='flex flex-col'>
                  <Text className='text-xs text-[#38383A99]'>Date</Text>
                  <Text className='font-semibold'>
                    {label === 'Kitchen Studio'
                      ? dayjs(item?.date)
                          .add(5, 'hours')
                          .add(30, 'minutes')
                          .format('MMM D, YYYY')
                      : dayjs(item?.event_start_date)
                          .add(5, 'hours')
                          .add(30, 'minutes')
                          .format('MMM D, YYYY')}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text className='text-xs text-[#38383A99]'>Time</Text>
                  <Text className='font-semibold'>
                    {label === 'Kitchen Studio'
                      ? item?.time
                      : `${dayjs(item?.event_start_date)
                          .add(5, 'hours')
                          .add(30, 'minutes')
                          .format('h:m A')} -
                        ${dayjs(item?.event_end_date)
                          .add(5, 'hours')
                          .add(30, 'minutes')
                          .format('h:m A')}`}
                  </Text>
                </div>
                {label === 'Event Details' && item?.quantity && (
                  <div className='flex flex-col'>
                    <Text className='text-xs text-[#38383A99]'>
                      No. of Seat(s)
                    </Text>
                    <Text className='font-semibold'>
                      {`${Number(item?.quantity)} ${
                        Number(item?.quantity) === 1 ? 'Seat' : 'Seats'
                      } Booked`}
                    </Text>
                  </div>
                )}
              </div>
              {(item?.location || item?.event_location) && (
                <div className='flex flex-col'>
                  <Text className='text-xs text-[#38383A99]'>Venue</Text>
                  <Text className='font-semibold'>
                    {item?.location || item?.event_location}
                  </Text>
                </div>
              )}
              <Text className='text-xs text-[#38383A99]'>
                You will receive a confirmation via SMS, Whatsapp or Email
                depending on your communication preference.
              </Text>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={showRemoveItemModal}
        onClose={onRemoveItemModalClose}
        id='remove-item-modal'
        title='Remove item?'>
        <RemoveItem
          onClose={onRemoveItemModalClose}
          {...item}
          isGreetingCard={isGreetingCard}
        />
      </Modal>

      <Modal
        show={showShelfItemModal}
        onClose={onShelfItemModalClose}
        id='shelf-item-modal'
        title='Move Item to Shelf?'>
        <ShelfItem onClose={onShelfItemModalClose} {...item} />
      </Modal>

      <Modal
        show={showGiftBoxProductsModal}
        onClose={onGiftBoxProductsModalClose}
        id='gift-box-products-modal'
        title='Products in Gift Box'>
        <GiftBoxProductsModal
          giftBoxItem={item}
          giftBoxProducts={getGiftBoxProducts()}
        />
      </Modal>

      <Modal
        show={showBentoBoxProductsModal}
        onClose={onBentoBoxProductsModalClose}
        id='quartet-box-products-modal'
        title='Products in Quartet Box'>
        <BentoBoxProductsModal item={item} />
      </Modal>
    </>
  );
};

export default CheckoutProductCard;
